import { useEffect, useMemo, useRef, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { Button, ErrorNote, Spinner } from "./primitives";
import { api } from "../lib/api";
import {
  KINDS,
  companyDrawing,
  networkDrawing,
  shortName,
  type DrawNode,
  type Drawing,
  type Tip,
} from "../lib/memoryGraph";
import type { MemoryCompany, MemoryGraph as Graph } from "../lib/types";

/**
 * The knowledge graph drawn: the company network, clustered, and how well
 * each engagement's findings are corroborated (`core/memory/graph_explorer`).
 *
 * From `FilesForNewFeatures/GraphExplorer`, a standalone page with its own
 * palette. Here the clusters take the brand's region greys (`--data-1..5`,
 * generated from `brand.json`), and orange is spent on the one thing the
 * reader picked — a cluster from the legend, or the company typed into the
 * box — never as a cluster's colour. The server reads as the person asking,
 * so a private engagement of somebody else's is not in this drawing at all.
 *
 * Typing a company's name redraws the network as that company and everything
 * it is joined to; clearing the box brings the network back. Every circle
 * and every line says what it is on hover.
 *
 * The layout is a small force simulation written straight to the SVG, as the
 * original did: O(n²) a step, comfortable to a few hundred companies, and not
 * worth a charting library this bundle deliberately does not carry.
 */
export function MemoryGraph() {
  const graph = useQuery({ queryKey: ["memory-graph"], queryFn: api.memoryGraph });
  const [typed, setTyped] = useState("");
  const wanted = useSettled(typed.trim(), 250);
  const asking = wanted.length >= MIN_TYPED;
  const company = useQuery({
    queryKey: ["memory-company", wanted],
    queryFn: () => api.memoryCompany(wanted),
    enabled: asking && graph.isSuccess,
    // Typing on past a match keeps the drawing until the next answer, rather
    // than flashing the network between two keystrokes.
    placeholderData: keepPreviousData,
  });

  if (graph.isLoading) return <Spinner label="Drawing what memory holds…" />;
  if (graph.error) return <ErrorNote error={graph.error} />;
  if (!graph.data) return null;
  const shown = asking && company.data?.company ? company.data : null;
  return (
    <div className="space-y-8">
      <section className="space-y-2">
        <h2 className="text-base font-semibold">Company network</h2>
        <p className="text-sm text-ink-2">
          {shown
            ? `${shown.company} and everything memory records it joined to. Hover a circle or a line to read it; drag a circle to move it.`
            : `${graph.data.meta.companies} companies, linked where a run recorded them as competitors or where findings named them together. Pick a cluster to light it, type a company to see all it is joined to, hover a circle or a line to read it.`}
        </p>
        <Finder
          typed={typed}
          onType={setTyped}
          asking={asking}
          answer={asking ? company.data : undefined}
          loading={asking && company.isFetching && !company.data}
          error={asking ? company.error : null}
        />
        {shown ? (
          <CompanyView company={shown} minComention={graph.data.meta.min_comention} />
        ) : (
          <Network graph={graph.data} />
        )}
        <p className="text-xs text-muted">
          {shown
            ? "Relations as the runs recorded them, and companies named beside it in findings."
            : graph.data.meta.method === "leiden"
              ? `Clusters: Leiden over the network drawn, seed ${graph.data.meta.seed}.`
              : "Clusters: the last ones memory stored — the graph service could not cluster this drawing."}{" "}
          It shows what you may see: shared findings and your own private material.
        </p>
      </section>
      <Evidence graph={graph.data} />
    </div>
  );
}

/** Below this the box is still being typed into, not asked anything. */
const MIN_TYPED = 2;

/** `value`, once it has stopped changing for `ms` — the box asks on a pause. */
function useSettled(value: string, ms: number): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return value === "" ? "" : settled;
}

/** The box, and what it found: one company, several to pick from, or none. */
function Finder({
  typed,
  onType,
  asking,
  answer,
  loading,
  error,
}: {
  typed: string;
  onType: (value: string) => void;
  asking: boolean;
  answer: MemoryCompany | undefined;
  loading: boolean;
  error: unknown;
}) {
  const others = answer ? answer.candidates.filter((name) => name !== answer.company) : [];
  const relations = answer?.links.reduce((n, l) => n + l.relations.length, 0) ?? 0;
  const together = answer?.links.filter((l) => l.findings > 0).length ?? 0;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          className="field w-full max-w-md py-1.5"
          placeholder="Show one company — type its name"
          aria-label="Show one company and everything it is joined to"
          value={typed}
          onChange={(e) => onType(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") onType("");
          }}
        />
        {answer?.company && (
          <Button size="sm" variant="ghost" onClick={() => onType("")}>
            Show the whole network
          </Button>
        )}
      </div>
      {asking && (
        <div className="space-y-1.5 text-xs text-ink-2" aria-live="polite">
          {error ? (
            <ErrorNote error={error} />
          ) : loading ? (
            <p className="text-muted">Looking…</p>
          ) : answer?.company ? (
            <p>
              <span className="font-medium text-ink">{answer.company}</span>:{" "}
              {answer.links.length === 0
                ? "memory records nothing it is joined to yet."
                : `${answer.links.length} joined to it — ${relations} recorded ${relations === 1 ? "relation" : "relations"}${together ? `, ${together} named beside it in findings` : ""}.`}
            </p>
          ) : answer && answer.candidates.length === 0 ? (
            <p>No company in memory matches &ldquo;{answer.query}&rdquo;.</p>
          ) : answer ? (
            <p>
              {answer.candidates.length >= 8 ? "More than seven" : answer.candidates.length} companies
              match &ldquo;{answer.query}&rdquo; — pick one:
            </p>
          ) : null}
          {others.length > 0 && !loading && !error && (
            <div className="flex flex-wrap items-center gap-1.5">
              {answer?.company && <span className="text-muted">Also matching:</span>}
              {others.map((name) => (
                <Button key={name} size="sm" variant="secondary" onClick={() => onType(name)}>
                  {name}
                </Button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** The whole network, with the cluster legend that lights one. */
function Network({ graph }: { graph: Graph }) {
  const drawing = useMemo(() => networkDrawing(graph), [graph]);
  const [picked, setPicked] = useState<number | null>(null);
  const counts = useMemo(
    () => graph.groups.map((_, i) => graph.nodes.filter((n) => n[1] === i).length),
    [graph],
  );

  if (graph.nodes.length === 0) {
    return (
      <p className="py-10 text-center text-sm text-muted">
        No company in memory is linked to another yet. Competitors recorded by a run, or
        two companies named in the same finding, are what draws a line. A company typed
        into the box is drawn with everything it is joined to.
      </p>
    );
  }
  return (
    <>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-ink-2">
        {graph.groups.map((name, i) => (
          <button
            key={name}
            type="button"
            onClick={() => setPicked(picked === i ? null : i)}
            aria-pressed={picked === i}
            className={`flex items-center gap-1.5 ${picked === i ? "font-medium text-ink" : "hover:text-ink"}`}
          >
            <Swatch
              fill={picked === i ? "var(--signal)" : fillOf(i, graph.groups.length)}
              ring={picked === i ? "var(--signal)" : ringOf(i, graph.groups.length)}
            />
            {name}
            <span className="font-mono tabular-nums text-muted">{counts[i]}</span>
          </button>
        ))}
      </div>
      <ForceGraph
        drawing={drawing}
        picked={picked}
        label="Network of companies in memory, grouped by cluster"
      />
      <LineLegend recorded="competitors in the ledger" minComention={graph.meta.min_comention}>
        <span>circle size = findings</span>
      </LineLegend>
    </>
  );
}

/** One company at the centre, and what it is joined to, by kind. */
function CompanyView({ company, minComention }: { company: MemoryCompany; minComention: number }) {
  const drawing = useMemo(() => companyDrawing(company), [company]);
  const present = useMemo(() => {
    const counts = KINDS.map(() => 0);
    drawing.nodes.forEach((n) => {
      if (!n.centre) counts[n.group] = (counts[n.group] ?? 0) + 1;
    });
    return counts;
  }, [drawing]);
  return (
    <>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-ink-2">
        <span className="flex items-center gap-1.5 font-medium text-ink">
          <Swatch fill="var(--signal)" ring="var(--signal)" />
          {company.company}
        </span>
        {KINDS.map((kind, i) =>
          present[i] ? (
            <span key={kind.name} className="flex items-center gap-1.5">
              <Swatch fill={fillOf(i, KINDS.length)} ring={ringOf(i, KINDS.length)} />
              {kind.name}
              <span className="font-mono tabular-nums text-muted">{present[i]}</span>
            </span>
          ) : null,
        )}
      </div>
      <ForceGraph
        drawing={drawing}
        picked={null}
        label={`${company.company} and everything memory records it joined to`}
      />
      <LineLegend recorded="a relation a run recorded" minComention={minComention}>
        <span>circle size = how much joins them</span>
      </LineLegend>
    </>
  );
}

function Swatch({ fill, ring }: { fill: string; ring: string }) {
  return (
    <span
      className="size-2.5 rounded-full border"
      style={{ background: fill, borderColor: ring }}
      aria-hidden
    />
  );
}

function LineLegend({
  recorded,
  minComention,
  children,
}: {
  recorded: string;
  minComention: number;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted">
      <span className="flex items-center gap-1.5">
        <svg width="22" height="8" aria-hidden="true">
          <line x1="0" y1="4" x2="22" y2="4" stroke="var(--text-muted)" strokeWidth="1.5" />
        </svg>
        {recorded}
      </span>
      <span className="flex items-center gap-1.5">
        <svg width="22" height="8" aria-hidden="true">
          <line x1="0" y1="4" x2="22" y2="4" stroke="var(--text-muted)" strokeWidth="1.5" strokeDasharray="3 3" />
        </svg>
        named together in {minComention}+ findings
      </span>
      {children}
    </div>
  );
}

/**
 * A group's fill: the region greys, ordered so neighbours in the legend
 * differ most — darkest, dark, mid, light. **"Other" is a hollow ring**, not
 * the fifth grey: drawn filled it was one step from the light group and
 * read as the same group, and as the background it is, a ring says so.
 */
const GROUP_FILLS = ["var(--data-1)", "var(--data-4)", "var(--data-2)", "var(--data-3)"];

function fillOf(group: number, groups: number): string {
  return group === groups - 1 ? "var(--surface-1)" : (GROUP_FILLS[group] ?? "var(--data-3)");
}

function ringOf(group: number, groups: number): string {
  return group === groups - 1 ? "var(--data-3)" : "var(--surface-1)";
}

const HEIGHT = 520;

/** A line is a pixel or two wide; this is how wide it is to a pointer. */
const LINE_HIT_WIDTH = 10;

interface Body extends DrawNode {
  id: number;
  deg: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  fx?: number | null;
  fy?: number | null;
  el?: SVGCircleElement;
  tx?: SVGTextElement;
}

interface Edge {
  i: number;
  s: Body;
  t: Body;
  length: number;
  el?: SVGLineElement;
  hit?: SVGLineElement;
  tip: Tip;
}

/** A pop-up's content, built as DOM: the simulation owns this subtree. */
function fillTip(box: HTMLDivElement, tip: Tip) {
  box.replaceChildren();
  const title = document.createElement("div");
  title.className = "font-medium text-ink";
  title.textContent = tip.title;
  box.append(title);
  for (const line of tip.lines) {
    if (!line.text) continue;
    const row = document.createElement("div");
    row.className =
      line.tone === "ink"
        ? "mt-1 text-ink"
        : line.tone === "muted"
          ? "text-muted"
          : line.tone === "mono"
            ? "font-mono tabular-nums"
            : "";
    row.textContent = line.text;
    box.append(row);
  }
}

/**
 * The drawing: circles, lines and their pop-ups, laid out by a small force
 * simulation. `picked` lights one legend group in the orange; a centre node
 * takes it regardless, and stays pinned where it is put.
 */
function ForceGraph({
  drawing,
  picked,
  label,
}: {
  drawing: Drawing;
  picked: number | null;
  label: string;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const svg = useRef<SVGSVGElement>(null);
  const tip = useRef<HTMLDivElement>(null);
  const pickedRef = useRef<number | null>(picked);
  const paintRef = useRef<() => void>(() => {});

  useEffect(() => {
    pickedRef.current = picked;
    paintRef.current();
  }, [picked]);

  useEffect(() => {
    const box = wrap.current;
    const canvas = svg.current;
    const tooltip = tip.current;
    if (!box || !canvas || !tooltip || drawing.nodes.length === 0) return;
    const W = box.clientWidth;
    const H = HEIGHT;
    canvas.setAttribute("viewBox", `0 0 ${W} ${H}`);
    canvas.replaceChildren();
    const NS = "http://www.w3.org/2000/svg";
    const make = <K extends keyof SVGElementTagNameMap>(
      tag: K,
      attrs: Record<string, string | number>,
      parent: Element,
    ): SVGElementTagNameMap[K] => {
      const el = document.createElementNS(NS, tag);
      for (const key in attrs) el.setAttribute(key, String(attrs[key]));
      parent.appendChild(el);
      return el;
    };

    const G = drawing.groups.length;
    const nodes: Body[] = drawing.nodes.map((n, id) => ({
      ...n,
      id,
      deg: 0,
      vx: 0,
      vy: 0,
      x: n.x0 * W + (n.centre ? 0 : (Math.random() - 0.5) * 60),
      y: n.y0 * H + (n.centre ? 0 : (Math.random() - 0.5) * 60),
      fx: n.centre ? n.x0 * W : null,
      fy: n.centre ? n.y0 * H : null,
    }));
    const links: Edge[] = drawing.links.flatMap((l, i) => {
      const a = nodes[l.s];
      const b = nodes[l.t];
      return a && b ? [{ i, s: a, t: b, length: l.length, tip: l.tip }] : [];
    });
    links.forEach((l) => { l.s.deg++; l.t.deg++; });
    const centre = nodes.find((n) => n.centre);
    const adjacent = new Set(links.flatMap((l) => [`${l.s.id}-${l.t.id}`, `${l.t.id}-${l.s.id}`]));
    const radius = (n: Body) => n.r;

    const gL = make("g", {}, canvas);
    const gH = make("g", {}, canvas);
    const gN = make("g", {}, canvas);
    const gT = make("g", {}, canvas);
    links.forEach((l) => {
      const spec = drawing.links[l.i]!;
      l.el = make("line", {
        stroke: "var(--text-muted)",
        "stroke-width": spec.width,
        "stroke-opacity": 0.5,
      }, gL);
      // A relation the ledger recorded is solid; a shared mention dashed.
      if (spec.dashed) l.el.setAttribute("stroke-dasharray", "3 3");
      l.hit = make("line", {
        stroke: "transparent",
        "stroke-width": LINE_HIT_WIDTH,
        "pointer-events": "stroke",
      }, gH);
    });
    nodes.forEach((n) => {
      n.el = make("circle", {
        r: radius(n), fill: fillOf(n.group, G), stroke: ringOf(n.group, G), "stroke-width": 1.5,
      }, gN);
      n.el.style.cursor = "grab";
      if (n.labelled) {
        n.tx = make("text", { "text-anchor": "middle", class: n.centre ? "text-sm" : "text-xs" }, gT);
        n.tx.textContent = shortName(n.name);
        n.tx.style.fill = n.centre ? "var(--text-primary)" : "var(--text-secondary)";
        n.tx.style.pointerEvents = "none";
      }
    });

    const tick = (alpha: number) => {
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i]!;
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j]!;
          let dx = b.x - a.x, dy = b.y - a.y, d2 = dx * dx + dy * dy;
          if (d2 < 1) { dx = Math.random() - 0.5; dy = Math.random() - 0.5; d2 = 1; }
          if (d2 > 160000) continue;
          const w = (-36 * alpha) / d2;
          a.vx += dx * w; a.vy += dy * w; b.vx -= dx * w; b.vy -= dy * w;
        }
      }
      links.forEach(({ s, t, length }) => {
        let dx = t.x + t.vx - s.x - s.vx, dy = t.y + t.vy - s.y - s.vy;
        const d = Math.sqrt(dx * dx + dy * dy) || 1;
        const k = ((d - length) / d) * alpha * 0.7;
        dx *= k; dy *= k;
        const bias = s.deg / (s.deg + t.deg);
        t.vx -= dx * bias; t.vy -= dy * bias; s.vx += dx * (1 - bias); s.vy += dy * (1 - bias);
      });
      nodes.forEach((n) => { n.vx += (W / 2 - n.x) * 0.05 * alpha; n.vy += (H / 2 - n.y) * 0.08 * alpha; });
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i]!;
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j]!;
          const dx = b.x - a.x, dy = b.y - a.y, min = radius(a) + radius(b) + 1.5, d2 = dx * dx + dy * dy;
          if (d2 < min * min) {
            const d = Math.sqrt(d2) || 1, p = ((min - d) / d) * 0.35;
            a.x -= dx * p; a.y -= dy * p; b.x += dx * p; b.y += dy * p;
          }
        }
      }
      nodes.forEach((n) => {
        if (n.fx != null && n.fy != null) { n.x = n.fx; n.y = n.fy; n.vx = n.vy = 0; return; }
        n.vx *= 0.6; n.vy *= 0.6; n.x += n.vx; n.y += n.vy;
        const m = radius(n) + 2;
        n.x = Math.max(m, Math.min(W - m, n.x));
        n.y = Math.max(m, Math.min(H - m, n.y));
      });
    };
    const draw = () => {
      links.forEach(({ s, t, el, hit }) => {
        for (const line of [el!, hit!]) {
          line.setAttribute("x1", String(s.x)); line.setAttribute("y1", String(s.y));
          line.setAttribute("x2", String(t.x)); line.setAttribute("y2", String(t.y));
        }
      });
      nodes.forEach((n) => {
        n.el!.setAttribute("cx", String(n.x)); n.el!.setAttribute("cy", String(n.y));
        if (!n.tx) return;
        // Around a centre a name points away from it, so a ring of
        // neighbours does not stack its names on one another; elsewhere it
        // sits above its circle.
        const dx = centre && n !== centre ? n.x - centre.x : 0;
        const dy = centre && n !== centre ? n.y - centre.y : 0;
        const d = Math.hypot(dx, dy);
        if (d > 1 && Math.abs(dx / d) > 0.35) {
          const gap = radius(n) + 4;
          n.tx.setAttribute("text-anchor", dx > 0 ? "start" : "end");
          n.tx.setAttribute("x", String(n.x + (dx / d) * gap));
          n.tx.setAttribute("y", String(n.y + (dy / d) * gap + 4));
        } else {
          n.tx.setAttribute("text-anchor", "middle");
          n.tx.setAttribute("x", String(Math.max(75, Math.min(W - 75, n.x))));
          n.tx.setAttribute("y", String(dy > 0 ? n.y + radius(n) + 13 : n.y - radius(n) - 4));
        }
      });
    };

    // Hovering a company lights it and its neighbours, hovering a line lights
    // the line and its two ends; a group picked from the legend takes the
    // orange, and the rest of the graph steps back.
    const paint = (hover?: { node?: number; link?: number }) => {
      const group = pickedRef.current;
      const line = hover?.link != null ? links[hover.link] : undefined;
      nodes.forEach((n) => {
        const lit = hover?.node != null
          ? n.id === hover.node || adjacent.has(`${hover.node}-${n.id}`)
          : line
            ? n.id === line.s.id || n.id === line.t.id
            : group == null || n.group === group;
        n.el!.setAttribute("opacity", lit ? "1" : "0.15");
        n.el!.setAttribute(
          "fill",
          n.centre || (group != null && n.group === group) ? "var(--signal)" : fillOf(n.group, G),
        );
        if (n.centre) n.el!.setAttribute("stroke", "var(--signal)");
        if (n.tx) n.tx.setAttribute("opacity", lit ? "1" : "0.2");
      });
      links.forEach((l, i) => {
        const lit = hover?.node != null
          ? l.s.id === hover.node || l.t.id === hover.node
          : line
            ? i === hover?.link
            : group == null || (l.s.group === group && l.t.group === group);
        l.el!.setAttribute("stroke-opacity", lit ? (hover ? "0.9" : "0.6") : "0.05");
        l.el!.setAttribute("stroke", line && i === hover?.link ? "var(--text-primary)" : "var(--text-muted)");
      });
    };
    paintRef.current = () => paint();

    let alpha = 1, target = 0, frame = 0;
    let drag: Body | null = null;
    const run = () => {
      cancelAnimationFrame(frame);
      const step = () => {
        for (let k = 0; k < 2; k++) { tick(alpha); alpha += (target - alpha) * 0.0228; }
        draw();
        if (alpha > 0.005 || drag) frame = requestAnimationFrame(step);
      };
      frame = requestAnimationFrame(step);
    };
    canvas.style.touchAction = "none";
    canvas.style.userSelect = "none";
    draw();
    run();

    const point = (e: PointerEvent | MouseEvent): [number, number] => {
      const b = canvas.getBoundingClientRect();
      return [((e.clientX - b.left) * W) / b.width, ((e.clientY - b.top) * H) / b.height];
    };
    const show = (content: Tip) => {
      fillTip(tooltip, content);
      tooltip.style.opacity = "1";
    };
    const follow = (e: MouseEvent) => {
      const b = box.getBoundingClientRect();
      const w = tooltip.offsetWidth, h = tooltip.offsetHeight;
      let x = e.clientX - b.left + 12, y = e.clientY - b.top + 12;
      if (x + w > b.width) x = Math.max(0, e.clientX - b.left - w - 12);
      if (y + h > b.height) y = Math.max(0, e.clientY - b.top - h - 12);
      tooltip.style.left = `${x}px`;
      tooltip.style.top = `${y}px`;
    };
    const hide = () => {
      tooltip.style.opacity = "0";
      if (!drag) paint();
    };
    links.forEach((l, i) => {
      l.hit!.addEventListener("mouseenter", (e) => {
        if (drag) return;
        paint({ link: i });
        show(l.tip);
        follow(e);
      });
      l.hit!.addEventListener("mousemove", follow);
      l.hit!.addEventListener("mouseleave", hide);
    });
    nodes.forEach((n) => {
      n.el!.addEventListener("mouseenter", (e) => {
        if (drag) return;
        paint({ node: n.id });
        show(n.tip);
        follow(e);
      });
      n.el!.addEventListener("mousemove", follow);
      n.el!.addEventListener("mouseleave", hide);
      n.el!.addEventListener("pointerdown", (e) => {
        e.preventDefault();
        drag = n;
        tooltip.style.opacity = "0";
        [n.fx, n.fy] = point(e);
        target = 0.25;
        if (alpha < 0.25) alpha = 0.25;
        run();
      });
    });
    const move = (e: PointerEvent) => {
      if (drag) { e.preventDefault(); [drag.fx, drag.fy] = point(e); }
    };
    const end = () => {
      if (!drag) return;
      // The centre stays where it is put; anything else rejoins the layout.
      if (!drag.centre) drag.fx = drag.fy = null;
      drag = null;
      target = 0;
      paint();
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
    paint();
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
    };
  }, [drawing]);

  return (
    <div ref={wrap} className="relative rounded-lg border border-line bg-surface">
      <svg ref={svg} width="100%" height={HEIGHT} role="img" aria-label={label} />
      <div
        ref={tip}
        className="pointer-events-none absolute max-w-80 rounded-sm border border-line bg-surface px-2.5 py-1.5 text-xs text-ink-2 opacity-0"
      />
    </div>
  );
}

/** Findings by how many independent providers they rest on, per engagement. */
function Evidence({ graph }: { graph: Graph }) {
  const rows = graph.evidence
    .map(([target, playbook, p0, p1, p2, p3]) => {
      const total = p0 + p1 + p2 + p3;
      return { target, playbook, p0, p1, p2, p3, total, good: (p2 + p3) / total };
    })
    .sort((a, b) => b.good - a.good);
  const three = rows.reduce((t, r) => t + r.p3, 0);
  const two = rows.reduce((t, r) => t + r.p2, 0);
  const one = rows.reduce((t, r) => t + r.p1, 0);
  const none = rows.reduce((t, r) => t + r.p0, 0);
  const all = three + two + one + none || 1;
  const pct = (v: number) => `${Math.round((v / all) * 100)}%`;
  const segments: { key: "p3" | "p2" | "p1" | "p0"; label: string; fill: string }[] = [
    { key: "p3", label: "3+ providers", fill: "var(--data-1)" },
    { key: "p2", label: "2 providers", fill: "var(--data-2)" },
    { key: "p1", label: "1 provider", fill: "var(--data-3)" },
    {
      key: "p0",
      label: "no provider",
      fill: "repeating-linear-gradient(45deg, var(--data-3) 0 3px, transparent 3px 6px)",
    },
  ];

  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold">How corroborated each engagement is</h2>
      <p className="text-sm text-ink-2">
        Each engagement&apos;s findings by how many independent providers — the
        publishers, not the pages — they rest on, best corroborated first.
      </p>
      {rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted">No engagement in memory has findings yet.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              ["Findings", (three + two + one + none).toLocaleString()],
              ["2+ providers", pct(three + two)],
              ["1 provider", pct(one)],
              ["no provider", pct(none)],
            ].map(([label, value]) => (
              <div key={label} className="rounded-sm bg-surface-2 px-3 py-2">
                <div className="text-xs text-ink-2">{label}</div>
                <div className="font-mono text-lg tabular-nums text-ink">{value}</div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-2">
            {segments.map((s) => (
              <span key={s.key} className="flex items-center gap-1.5">
                <span className="size-2.5 rounded-sm border border-line" style={{ background: s.fill }} aria-hidden />
                {s.label}
              </span>
            ))}
          </div>
          <div className="space-y-1.5">
            {rows.map((r) => (
              <div
                key={`${r.target}·${r.playbook}`}
                className="grid grid-cols-[minmax(0,14rem)_minmax(0,1fr)_4.5rem] items-center gap-3 text-xs"
              >
                <div className="truncate" title={`${r.target} · ${r.playbook}`}>
                  {r.target} <span className="text-muted">{r.playbook}</span>
                </div>
                <div className="flex h-3.5 gap-0.5 overflow-hidden">
                  {segments.map((s) =>
                    r[s.key] ? (
                      <div
                        key={s.key}
                        title={`${s.label}: ${r[s.key]}`}
                        style={{ flex: `${r[s.key]} 0 0`, background: s.fill }}
                      />
                    ) : null,
                  )}
                </div>
                <div className="text-right font-mono tabular-nums text-ink-2">
                  {Math.round(r.good * 100)}% · {r.total}
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted">
            &ldquo;No provider&rdquo; means no source with a provider edge is attached
            to the finding — it can be a missing edge rather than weak research.
          </p>
        </>
      )}
    </section>
  );
}
